import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { createSocket } from '@/services/socket';
import { useAuthStore } from '@/store/authStore';
import { useInvalidateNotifications } from './hooks';
import type { AppNotification, NotificationLevel } from './types';

const LEVEL_VARIANT: Record<NotificationLevel, 'info' | 'success' | 'warning' | 'error'> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

/**
 * Subscribes to the backend `/notifications` Socket.IO namespace and refreshes the
 * notification queries when a push arrives, toasting the new item (spec §25). Runs
 * only while authenticated; reconnects use a fresh access token via the socket
 * factory's auth callback.
 */
export function useNotificationSocket() {
  const status = useAuthStore((s) => s.status);
  const invalidate = useInvalidateNotifications();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (status !== 'authenticated') return;

    const socket = createSocket('/notifications');
    socket.on('notification', (notification: AppNotification) => {
      void invalidate();
      enqueueSnackbar(notification.title, {
        variant: LEVEL_VARIANT[notification.level] ?? 'info',
      });
    });
    socket.connect();

    return () => {
      socket.off('notification');
      socket.disconnect();
    };
    // invalidate/enqueueSnackbar are stable; re-run only when auth status flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
}
